import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { Tabs } from "@package/subframe/components/Tabs";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherAlertCircle,
  FeatherHistory,
  FeatherMoreVertical,
  FeatherPenTool,
  FeatherSearch,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { FollowUpActionsTable } from "@/components/follow-up-actions/FollowUpActionsTable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useUpdateActionStatusCommandControllerUpdateStatus } from "@/generated/follow-up-actions/follow-up-actions";
import { UpdateActionStatusDtoStatus } from "@/generated/model/updateActionStatusDtoStatus";
import { useFollowUpActionsData } from "@/hooks/use-follow-up-actions-data";
import type { SortDir } from "@/lib/category-display";
import {
  DEFAULT_SORT,
  type FollowUpActionSortColumn,
  type FollowUpActionsTab,
  PAGE_SIZE,
} from "@/lib/follow-up-actions/follow-up-actions-list";
import { invalidateFollowUpActionQueries } from "@/lib/follow-up-actions/invalidate-follow-up-action-queries";

const routeApi = getRouteApi("/_authenticated/follow-up-actions/");

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-neutral-100 px-2 text-caption-bold font-caption-bold text-neutral-700">
      {count}
    </span>
  );
}

export function FollowUpActionsPage() {
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const { selectedNurseryId } = useNursery();
  const queryClient = useQueryClient();

  const tab = (search.tab as FollowUpActionsTab) ?? "open";
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<
    FollowUpActionSortColumn | undefined
  >(undefined);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];

  const updateStatus = useUpdateActionStatusCommandControllerUpdateStatus();

  // Debounce the search box so each keystroke doesn't hit the API.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const effectiveSort = sortColumn
    ? { column: sortColumn, dir: sortDir }
    : DEFAULT_SORT[tab];

  const { items, total, isLoading, counts } = useFollowUpActionsData({
    nurseryId: selectedNurseryId ?? undefined,
    tab,
    categoryId,
    search: searchTerm,
    sortBy: effectiveSort.column,
    sortDir: effectiveSort.dir,
    page,
    pageSize,
  });

  const setTab = (next: FollowUpActionsTab) => {
    setPage(0);
    setSortColumn(undefined);
    navigate({ to: "/follow-up-actions", search: { tab: next } });
  };

  const handleSort = (column: FollowUpActionSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  const invalidateActions = () => invalidateFollowUpActionQueries(queryClient);

  const changeStatus = async (
    actionId: string,
    status: UpdateActionStatusDtoStatus,
    successMessage: string,
  ) => {
    try {
      await updateStatus.mutateAsync({ actionId, data: { status } });
      toast.success(successMessage);
      await invalidateActions();
    } catch {
      toast.error("Failed to update the action");
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Follow-up actions
          </span>
          <span className="text-body font-body text-subtext-color">
            View and manage actions that require follow-up.
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs>
        <Tabs.Item
          active={tab === "open"}
          icon={<FeatherAlertCircle />}
          onClick={() => setTab("open")}
        >
          Open
          <CountBadge count={counts.open} />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "requires-sign-off"}
          icon={<FeatherPenTool />}
          onClick={() => setTab("requires-sign-off")}
        >
          Requires sign off
          <CountBadge count={counts.requiresSignOff} />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "history"}
          icon={<FeatherHistory />}
          onClick={() => setTab("history")}
        >
          History
        </Tabs.Item>
      </Tabs>

      <div className="flex w-full flex-col items-start gap-4 pb-8">
        {/* Filters */}
        <div className="flex w-full flex-wrap items-center gap-3">
          <Select
            placeholder="All categories"
            value={categoryId ?? "ALL"}
            onValueChange={(val) => {
              setCategoryId(val === "ALL" ? undefined : val);
              setPage(0);
            }}
          >
            <Select.Item value="ALL">All categories</Select.Item>
            {categories.map((category) => (
              <Select.Item key={category.id} value={category.id}>
                {category.name}
              </Select.Item>
            ))}
          </Select>
          <div className="grow" />
          <TextField
            className="h-auto w-full lg:w-72"
            variant="outline"
            size="base"
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder="Search actions"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </TextField>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-subtext-color">
              No follow-up actions to show for this view.
            </span>
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <FollowUpActionsTable
                items={items}
                categories={categories}
                isHistory={tab === "history"}
                activeColumn={effectiveSort.column}
                activeDir={effectiveSort.dir}
                onSort={handleSort}
                onView={(id) => navigate({ to: `/follow-up-actions/${id}` })}
                renderRowMenu={(action) => (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <IconButton
                        icon={<FeatherMoreVertical />}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tab === "requires-sign-off" ? (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.COMPLETED,
                                "Action signed off",
                              )
                            }
                          >
                            Sign off
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.OPEN,
                                "Action returned to open",
                              )
                            }
                          >
                            Return to open
                          </DropdownMenuItem>
                        </>
                      ) : tab === "open" ? (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.COMPLETED,
                                "Action marked complete",
                              )
                            }
                          >
                            Mark complete
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              changeStatus(
                                action.id,
                                UpdateActionStatusDtoStatus.CANCELLED,
                                "Action cancelled",
                              )
                            }
                          >
                            Cancel action
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <DropdownMenuItem
                          onClick={() =>
                            navigate({ to: `/follow-up-actions/${action.id}` })
                          }
                        >
                          View details
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              />
            </TableScrollContainer>

            <Pagination
              page={page + 1}
              pageSize={pageSize}
              totalCount={total}
              onPageChange={(next) => setPage(next - 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              itemNoun="actions"
            />
          </>
        )}
      </div>
    </div>
  );
}

export default FollowUpActionsPage;
